<script>
import { mapActions } from "vuex";
import moment from "@/utils/moment";
import Phone from "@/components/_Shared/Forms/Phone";
import Description from "@/components/_Shared/Forms/Description";

export default {
  components: {
    Phone,
    Description
  },

  props: {
    professional: {
      required: true,
      type: Object
    }
  },

  data() {
    return {
      birthdate: null,
      descriptionModal: false,
      personalPreloader: false,
      statusPreloader: false
    };
  },

  methods: {
    ...mapActions("professionals", ["updateProfessional", "setEvent"]),

    setBirthdate() {
      if (this.professional.account.profile.birthdate) {
        this.birthdate = moment(
          this.professional.account.profile.birthdate
        ).format("DD/MM/Y");
      }
    },

    formatResponse(responses) {
      return responses.map(response => {
        return response.status;
      });
    },

    coverageSuccessStatusRange(status) {
      return status.every(status => status >= 200 || status <= 202);
    },

    async updatePersonalData() {
      this.personalPreloader = true;

      try {
        let responses = await this.updateProfessional(this.professional);

        if (this.coverageSuccessStatusRange(this.formatResponse(responses))) {
          this.$toast.success("Dados do profissional atualizados!");
        } else {
          this.$toast.error(
            "Não foi possível atualizar os dados do profissional!"
          );
        }
      } catch (e) {
        this.$toast.error(
          "Não foi possível atualizar os dados do profissional!"
        );
      }

      this.personalPreloader = false;
    },

    changeStatus(to) {
      this.descriptionModal = true;
      this.setEvent(to);
    },

    closeDialog() {
      this.descriptionModal = false;
      this.$emit("refresh");
    }
  },

  watch: {
    birthdate(val) {
      this.professional.account.profile.birthdate =
        val.length === 10 ? moment(val, "DD/MM/Y").format("Y-MM-DD") : "";
    }
  },

  mounted() {
    this.setBirthdate();
  }
};
</script>

<template>
  <div>
    <v-layout row class="title-form">PESSOAL</v-layout>

    <!-- FIRST AND LAST NAME -->
    <v-layout align-center justify-space-between row fill-height wrap>
      <v-flex px-1 md6 xs12>
        <v-text-field
          color="primary"
          name="name"
          label="nome"
          v-model="professional.account.first_name"
          custom-classes="capitalize"
          v-validate.disabled="'required|alpha_spaces'"
          :error-messages="errors.first('name')"
          :error="errors.has('name')"
        />
      </v-flex>

      <v-flex px-1 md6 xs12>
        <v-text-field
          name="last_name"
          label="sobrenome"
          v-model="professional.account.last_name"
          v-validate="'required|alpha_spaces'"
          :error-messages="errors.first('last_name')"
          :error="errors.has('last_name')"
        />
      </v-flex>
    </v-layout>

    <!-- MEI AND CPF -->
    <v-layout align-center justify-space-between row fill-height wrap>
      <v-flex px-1 md6 xs12>
        <v-text-field
          name="mei"
          label="mei"
          v-model="professional.mei"
          mask="##.###.###/####-##"
          v-validate="'numeric'"
          :error-messages="errors.first('mei')"
          :error="errors.has('mei')"
        />
      </v-flex>

      <v-flex px-1 md6 xs12>
        <v-text-field
          name="cpf"
          label="cpf"
          v-model="professional.cpf"
          mask="###.###.###-##"
          v-validate="'numeric|required'"
          :error-messages="errors.first('cpf')"
          :error="errors.has('cpf')"
        />
      </v-flex>
    </v-layout>

    <!-- EMAIL AND BIRTHDATE -->
    <v-layout align-center justify-space-between row fill-height wrap>
      <v-flex px-1 md6 xs12>
        <v-text-field
          name="email"
          v-model="professional.account.email"
          label="email"
          :error="errors.has('email')"
          :error-messages="errors.first('email')"
          v-validate.disabled="'required|email'"
        />
      </v-flex>

      <v-flex px-1 md6 xs12>
        <v-text-field
          name="birthdate"
          label="data de nascimento"
          v-model="birthdate"
          v-mask="'##/##/####'"
          v-validate.disabled="'required'"
          :error-messages="errors.first('birthdate')"
          :error="errors.has('birthdate')"
        />
      </v-flex>
    </v-layout>

    <!-- PHONES -->
    <v-layout align-center justify-space-between row fill-height wrap>
      <v-flex px-1 md6 xs12>
        <phone
          v-model="professional.account.phones[0]"
          :phone="professional.account.phones[0]"
          label="celular"
        />
      </v-flex>

      <v-flex px-1 md6 xs12>
        <phone
          v-model="professional.account.phones[1]"
          :phone="professional.account.phones[1]"
          label="outro celular"
        />
      </v-flex>
    </v-layout>

    <!-- STATE, CITY AND DISTRICT -->
    <v-layout align-center justify-space-between row fill-height wrap>
      <v-flex px-1 md6 xs12>
        <v-text-field
          name="cidade"
          v-model="professional.account.profile.city_name"
          label="cidade"
          :error-messages="errors.first('cidade')"
          :error="errors.has('cidade')"
          v-validate="'required|alpha_spaces'"
        />
      </v-flex>

      <v-flex px-1 md6 xs12>
        <v-text-field
          name="UF"
          v-model="professional.account.profile.state_name"
          label="UF"
          :error="errors.has('UF')"
          :error-messages="errors.first('UF')"
          v-validate="'required|alpha|length:2'"
        />
      </v-flex>
    </v-layout>

    <div class="group-button">
      <v-btn
        v-if="professional.account.status === 'active'"
        color="error"
        small
        :loading="statusPreloader === 'deactivate'"
        @click="changeStatus('deactivate')"
      >
        <span>inativar</span>
      </v-btn>

      <v-btn
        v-if="professional.account.status === 'inactive'"
        color="success"
        small
        :loading="statusPreloader === 'activate'"
        @click="changeStatus('activate')"
      >
        <span>ativar</span>
      </v-btn>

      <v-btn
        v-if="professional.account.status === 'inactive'"
        color="error"
        small
        :loading="statusPreloader === 'block'"
        @click="changeStatus('block')"
      >
        <span>bloquear</span>
      </v-btn>

      <v-btn
        v-if="professional.account.status === 'blocked'"
        color="warning"
        small
        :loading="statusPreloader === 'unblock'"
        @click="changeStatus('unblock')"
      >
        <span>desbloquear</span>
      </v-btn>

      <v-btn
        color="primary"
        small
        :loading="personalPreloader"
        @click="updatePersonalData"
      >
        <span>Atualizar dados cadastrais</span>
      </v-btn>
    </div>

    <description
      @click="$emit('selected', descriptionModal)"
      :visible="descriptionModal"
      :professional="this.professional"
      @close="closeDialog()"
    >
    </description>
  </div>
</template>

<style scoped>
.row {
  margin: 0;
}

.group-button {
  margin-top: 10px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}
</style>



// WEBPACK FOOTER //
// src/components/Professionals/Edit/FormData/PersonalData.vue